import messagesData from './messages.json';
import type { IUser } from './IUser';

export interface GameState {
    consecutiveDitches: number;
    lastVelocityY?: number;
    lastScore?: number;
    isTurnStart?: boolean;
    retributionFailed?: boolean;
    almostSkeech?: boolean;
    skeechAchieved?: boolean;
    skeechLeadsToRetribution?: boolean;
    skeechTiesSuddenDeath?: boolean;
    skeechEndsSuddenDeath?: boolean;
}

export interface Message {
    id: string;
    text: string[];
    type: 'general' | 'hint' | 'multi-part' | 'story' | 'roll-feedback';  // Added 'roll-feedback' type
    priority?: number;  // Message priority (0-10, higher = more important)
    progression?: {
        minGames?: number;
        maxGames?: number;
        minRolls?: number;
        maxRolls?: number;
        minSkeeches?: number;
        maxSkeeches?: number;
    };
    trigger?: string;
    triggerValue?: any;
    // 📖 STORY MESSAGE CONTROL
    story?: {
        sequence: number;    // Order in the story (1, 2, 3, etc.)
        storyId: string;     // Group identifier for related story parts
        maxGapRolls?: number; // Max rolls between story parts before reset
    };
    // 🎯 ROLL FEEDBACK CONTROL
    rollFeedback?: {
        zone: 'left' | 'right' | 'chevron-left' | 'chevron-right';  // Dedicated feedback zones
        wordIndex?: number;  // For multi-word feedback (0=first word goes left, 1=second goes right)
    };
    // ✨ POSITIONING AND ANIMATION CONTROL
    positioning?: {
        mode?: 'random' | 'fixed' | 'staggered' | 'roll-zones';  // Added 'roll-zones' mode
        startX?: number;     // Starting X position (0-1 or absolute pixels)
        startY?: number;     // Starting Y position (0-1 or absolute pixels)
        verticalSpacing?: number;  // Space between words for staggered mode
        delay?: number;      // Delay between words (ms)
        duration?: number;   // How long each word stays (ms)
        color?: string;      // Text color override
        fontSize?: number;   // Font size override
        effect?: 'glow' | 'pulse' | 'fade';  // Animation effect override
    };
}

class MessageManager {
    private messages: Message[];
    private multipartMessageState: { messageId: string; part: number } | null = null;
    private currentMessagePriority: number = -1;
    private currentMessageEndTime: number = 0;
    private messageQueue: Array<{ message: Message; text: string; requestTime: number }> = [];

    // 🚫 MESSAGE RESERVATION SYSTEM - Prevent conflicts
    private reservedMessageSlot: {
        type: string;           // 'velocity', 'score', etc.
        priority: number;       // Expected priority
        reservedUntil: number;  // When reservation expires
    } | null = null;

    // 🎛️ MESSAGE FREQUENCY CONTROL
    public messageFrequency: number = 0.9; // 30% chance per roll (adjustable dial)
    private lastRandomMessageTime: number = 0;
    private minTimeBetweenRandom: number = 5000; // 5 seconds minimum between random messages

    // 📖 STORY MESSAGE TRACKING
    private storyStates: Map<string, {
        currentSequence: number;
        lastRollShown: number;
        totalRolls: number;
    }> = new Map();

    constructor() {
        this.messages = messagesData.messages as Message[];
    }

    public getMessage(stats: IUser, gameState: GameState): string | null {
        // Logic for multi-part messages
        if (this.multipartMessageState) {
            const message = this.messages.find(m => m.id === this.multipartMessageState!.messageId);
            if (message) {
                const nextPart = this.multipartMessageState.part;
                if (nextPart < message.text.length) {
                    this.multipartMessageState.part++;
                    return message.text[nextPart];
                } else {
                    this.multipartMessageState = null; // end of message
                }
            }
        }

        const triggered = this.messages.find(m => {
            if (m.trigger === 'consecutiveDitches' && gameState.consecutiveDitches >= m.triggerValue) {
                return true;
            }
            if (m.trigger === 'turn_start' && gameState.isTurnStart) {
                return true;
            }
            if (m.trigger === 'high_velocity' && gameState.lastVelocityY !== undefined && gameState.lastVelocityY < m.triggerValue) {
                return true;
            }
            if (m.trigger === 'medium_velocity' && gameState.lastVelocityY !== undefined && gameState.lastVelocityY < m.triggerValue && gameState.lastVelocityY >= -425) {
                return true;
            }
            if (m.trigger === 'score_3_points' && gameState.lastScore === 3) {
                return true;
            }
            if (m.trigger === 'score_5_points' && gameState.lastScore === 5) {
                return true;
            }
            if (m.trigger === 'score_1_point' && gameState.lastScore === 1) {
                return true;
            }
            if (m.trigger === 'retribution_failed' && gameState.retributionFailed) {
                return true;
            }
            if (m.trigger === 'almostSkeech' && gameState.almostSkeech) {
                return true;
            }
            if (m.trigger === 'skeechAchieved' && gameState.skeechAchieved) {
                return true;
            }
            if (m.trigger === 'skeechLeadsToRetribution' && gameState.skeechLeadsToRetribution) {
                return true;
            }
            if (m.trigger === 'skeechTiesSuddenDeath' && gameState.skeechTiesSuddenDeath) {
                return true;
            }
            if (m.trigger === 'skeechEndsSuddenDeath' && gameState.skeechEndsSuddenDeath) {
                return true;
            }
            return false;
        });

        if (triggered) {
            this.resetMultiPartMessage();
            if (triggered.type === 'multi-part') {
                this.multipartMessageState = { messageId: triggered.id, part: 1 };
                return triggered.text[0];
            }
            return triggered.text[0];
        }

        const availableMessages = this.messages.filter(m => {
            if (m.progression) {
                if (m.progression.minGames && ((stats.gamesPlayed ?? 0) ?? 0) < m.progression.minGames) {
                    return false;
                }
                if (m.progression.maxGames && ((stats.gamesPlayed ?? 0) ?? 0) > m.progression.maxGames) {
                    return false;
                }
                if (m.progression.minRolls && stats.rollsTotal < m.progression.minRolls) {
                    return false;
                }
                if (m.progression.maxRolls && stats.rollsTotal > m.progression.maxRolls) {
                    return false;
                }
                if (m.progression.minSkeeches && stats.numOfSkeeches < m.progression.minSkeeches) {
                    return false;
                }
                if (m.progression.maxSkeeches && stats.numOfSkeeches > m.progression.maxSkeeches) {
                    return false;
                }
            }
            // Do not show triggered messages as part of random rotation
            if (m.trigger) {
                return false;
            }

            return true;
        });

        if (availableMessages.length > 0) {
            const message = availableMessages[Math.floor(Math.random() * availableMessages.length)];
            if (message.type === 'multi-part') {
                this.multipartMessageState = { messageId: message.id, part: 1 };
                return message.text[0];
            }
            return message.text[0];
        }

        return null;
    }

    /**
     * 🎯 GET MESSAGE WITH POSITIONING DATA
     * Returns the full message object including positioning information
     */
    public getMessageWithPositioning(stats: IUser, gameState: GameState): { text: string; message: Message } | null {
        // Logic for multi-part messages
        if (this.multipartMessageState) {
            const message = this.messages.find(m => m.id === this.multipartMessageState!.messageId);
            if (message) {
                const nextPart = this.multipartMessageState.part;
                if (nextPart < message.text.length) {
                    this.multipartMessageState.part++;
                    return { text: message.text[nextPart], message };
                } else {
                    this.multipartMessageState = null; // end of message
                }
            }
        }

        const triggered = this.messages.find(m => {
            if (m.trigger === 'consecutiveDitches' && gameState.consecutiveDitches >= m.triggerValue) {
                return true;
            }
            if (m.trigger === 'turn_start' && gameState.isTurnStart) {
                return true;
            }
            if (m.trigger === 'high_velocity' && gameState.lastVelocityY !== undefined && gameState.lastVelocityY < m.triggerValue) {
                return true;
            }
            if (m.trigger === 'medium_velocity' && gameState.lastVelocityY !== undefined && gameState.lastVelocityY < m.triggerValue && gameState.lastVelocityY >= -425) {
                return true;
            }
            if (m.trigger === 'score_3_points' && gameState.lastScore === 3) {
                return true;
            }
            if (m.trigger === 'score_5_points' && gameState.lastScore === 5) {
                return true;
            }
            if (m.trigger === 'score_1_point' && gameState.lastScore === 1) {
                return true;
            }
            if (m.trigger === 'retribution_failed' && gameState.retributionFailed) {
                return true;
            }
            if (m.trigger === 'almostSkeech' && gameState.almostSkeech) {
                return true;
            }
            if (m.trigger === 'skeechAchieved' && gameState.skeechAchieved) {
                return true;
            }
            if (m.trigger === 'skeechLeadsToRetribution' && gameState.skeechLeadsToRetribution) {
                return true;
            }
            if (m.trigger === 'skeechTiesSuddenDeath' && gameState.skeechTiesSuddenDeath) {
                return true;
            }
            if (m.trigger === 'skeechEndsSuddenDeath' && gameState.skeechEndsSuddenDeath) {
                return true;
            }
            return false;
        });

        if (triggered) {
            this.resetMultiPartMessage();
            if (triggered.type === 'multi-part') {
                this.multipartMessageState = { messageId: triggered.id, part: 1 };
                return { text: triggered.text[0], message: triggered };
            }
            return { text: triggered.text[0], message: triggered };
        }

        const availableMessages = this.messages.filter(m => {
            if (m.progression) {
                if (m.progression.minGames && ((stats.gamesPlayed ?? 0) ?? 0) < m.progression.minGames) {
                    return false;
                }
                if (m.progression.maxGames && ((stats.gamesPlayed ?? 0) ?? 0) > m.progression.maxGames) {
                    return false;
                }
                if (m.progression.minRolls && stats.rollsTotal < m.progression.minRolls) {
                    return false;
                }
                if (m.progression.maxRolls && stats.rollsTotal > m.progression.maxRolls) {
                    return false;
                }
                if (m.progression.minSkeeches && stats.numOfSkeeches < m.progression.minSkeeches) {
                    return false;
                }
                if (m.progression.maxSkeeches && stats.numOfSkeeches > m.progression.maxSkeeches) {
                    return false;
                }
            }
            // Do not show triggered messages as part of random rotation
            if (m.trigger) {
                return false;
            }

            return true;
        });

        if (availableMessages.length > 0) {
            const message = availableMessages[Math.floor(Math.random() * availableMessages.length)];
            if (message.type === 'multi-part') {
                this.multipartMessageState = { messageId: message.id, part: 1 };
                return { text: message.text[0], message };
            }
            return { text: message.text[0], message };
        }

        return null;
    }

    public resetMultiPartMessage() {
        this.multipartMessageState = null;
    }

    /**
     * 🎯 PRIORITY-BASED MESSAGE SYSTEM
     * Handles message queuing with priority levels to prevent clutter
     */
    public getMessageWithPriority(stats: IUser, gameState: GameState): { text: string; message: Message } | null {
        const now = performance.now();

        // Continue roll-based multi-part sequence if active (time-based handled by BoardLighting)
        if (this.multipartMessageState && now < this.currentMessageEndTime && this.currentMessagePriority >= 0) {
            const message = this.messages.find(m => m.id === this.multipartMessageState!.messageId);
            if (message && !message.positioning?.delay) {
                // Roll-based multi-part - show next part on this call
                const nextPart = this.multipartMessageState.part;
                if (nextPart < message.text.length) {
                    this.multipartMessageState.part++;
                    return { text: message.text[nextPart], message };
                } else {
                    this.multipartMessageState = null;
                    this.currentMessagePriority = -1;
                }
            }
        }

        // Find triggered messages
        const triggeredMessages = this.messages.filter(m => this.isTriggered(m, gameState));


        // Get the highest priority triggered messages (with random selection for same priority)
        let bestMessage: Message | null = null;
        let bestText: string | null = null;
        let highestPriority = -1;
        let candidatesAtHighestPriority: Message[] = [];


        for (const message of triggeredMessages) {
            const priority = this.getMessagePriority(message);

            // Only consider if priority is higher than current or current has expired
            if (priority > this.currentMessagePriority || now >= this.currentMessageEndTime) {
                if (priority > highestPriority) {
                    // Found a higher priority message, reset candidates
                    highestPriority = priority;
                    candidatesAtHighestPriority = [message];
                } else if (priority === highestPriority) {
                    // Same priority, add to candidates for random selection
                    candidatesAtHighestPriority.push(message);
                }
            }
        }

        // Randomly select from the highest priority candidates
        if (candidatesAtHighestPriority.length > 0) {
            bestMessage = candidatesAtHighestPriority[Math.floor(Math.random() * candidatesAtHighestPriority.length)];
            bestText = bestMessage.text[0];
        }

        // Try story messages first (between triggered and random)
        if (!bestMessage && (this.currentMessagePriority < 0 || now >= this.currentMessageEndTime)) {
            const storyMessage = this.getNextStoryMessage(stats);
            if (storyMessage) {
                const storyPriority = 1; // Stories have low priority, can be interrupted
                if (this.isMessageSlotAvailable(storyPriority)) {
                    bestMessage = storyMessage;
                    bestText = storyMessage.text[0];
                    this.trackStoryProgress(storyMessage);
                }
            }
        }

        // If no story message, try random messages (with frequency control)
        if (!bestMessage && (this.currentMessagePriority < 0 || now >= this.currentMessageEndTime)) {
            // Check frequency and timing constraints
            const timeSinceLastRandom = now - this.lastRandomMessageTime;
            const shouldShowRandom = Math.random() < this.messageFrequency && timeSinceLastRandom > this.minTimeBetweenRandom;

            if (shouldShowRandom) {
                const availableMessages = this.getAvailableRandomMessages(stats);

                if (availableMessages.length > 0) {
                    const randomMessage = availableMessages[Math.floor(Math.random() * availableMessages.length)];
                    const randomPriority = this.getMessagePriority(randomMessage);


                    // Only show random message if slot is available
                    if (this.isMessageSlotAvailable(randomPriority)) {
                        bestMessage = randomMessage;
                        bestText = randomMessage.text[0];
                        this.lastRandomMessageTime = now; // Track timing
                    } else {
                    }
                }
            } else {
            }
        }

        if (bestMessage && bestText) {
            // Set up message timing
            let duration = bestMessage.positioning?.duration || 2000;

            // For time-based multi-part messages, calculate total duration
            if (bestMessage.type === 'multi-part' && bestMessage.positioning?.delay) {
                const delay = bestMessage.positioning.delay;
                const totalDelay = (bestMessage.text.length - 1) * delay;
                duration = Math.max(duration, totalDelay + 1000); // Add buffer after last word
            }

            this.currentMessagePriority = this.getMessagePriority(bestMessage);
            this.currentMessageEndTime = now + duration;

            // Handle multi-part setup - only for roll-based (no delay)
            if (bestMessage.type === 'multi-part' && !bestMessage.positioning?.delay) {
                this.multipartMessageState = { messageId: bestMessage.id, part: 1 };
            }

            return { text: bestText, message: bestMessage };
        }

        return null;
    }

    private isTriggered(message: Message, gameState: GameState): boolean {
        if (!message.trigger) return false;


        if (message.trigger === 'consecutiveDitches' && gameState.consecutiveDitches >= message.triggerValue) {
            return true;
        }
        if (message.trigger === 'turn_start' && gameState.isTurnStart) {
            return true;
        }
        if (message.trigger === 'high_velocity' && gameState.lastVelocityY !== undefined && gameState.lastVelocityY < message.triggerValue) {
            return true;
        }
        if (message.trigger === 'medium_velocity' && gameState.lastVelocityY !== undefined && gameState.lastVelocityY < message.triggerValue && gameState.lastVelocityY >= -425) {
            return true;
        }
        if (message.trigger === 'score_3_points' && gameState.lastScore === 3) {
            return true;
        }
        if (message.trigger === 'score_5_points' && gameState.lastScore === 5) {
            return true;
        }
        if (message.trigger === 'score_1_point' && gameState.lastScore === 1) {
            return true;
        }
        if (message.trigger === 'retribution_failed' && gameState.retributionFailed) {
            return true;
        }
        if (message.trigger === 'almostSkeech' && gameState.almostSkeech) {
            return true;
        }
        if (message.trigger === 'skeechAchieved' && gameState.skeechAchieved) {
            return true;
        }
        if (message.trigger === 'skeechLeadsToRetribution' && gameState.skeechLeadsToRetribution) {
            return true;
        }
        if (message.trigger === 'skeechTiesSuddenDeath' && gameState.skeechTiesSuddenDeath) {
            return true;
        }
        if (message.trigger === 'skeechEndsSuddenDeath' && gameState.skeechEndsSuddenDeath) {
            return true;
        }
        return false;
    }

    private getMessagePriority(message: Message): number {
        // Default priorities based on trigger type
        if (message.priority !== undefined) {
            return message.priority;
        }

        // Auto-assign priorities based on trigger type
        if (message.trigger === 'skeechAchieved') {
            return 10; // Critical: Skeech achievement (highest priority!)
        }
        if (message.trigger === 'skeechLeadsToRetribution') {
            return 11; // CRITICAL: Skeech-to-retribution transition (INTERRUPTS EVERYTHING!)
        }
        if (message.trigger === 'skeechTiesSuddenDeath') {
            return 11; // CRITICAL: Skeech ties game to sudden death (INTERRUPTS EVERYTHING!)
        }
        if (message.trigger === 'skeechEndsSuddenDeath') {
            return 11; // CRITICAL: Skeech ends sudden death game (INTERRUPTS EVERYTHING!)
        }
        if (message.trigger === 'almostSkeech') {
            return 6; // Achievement notifications: Skeech is possible!
        }
        if (message.trigger === 'score_5_points' || message.trigger === 'score_3_points' || message.trigger === 'score_1_point') {
            return 10; // Critical: Score feedback
        }
        if (message.trigger === 'high_velocity' || message.trigger === 'medium_velocity') {
            return 8; // Important: Velocity warnings
        }
        if (message.trigger === 'consecutiveDitches') {
            return 6; // Achievement notifications
        }
        if (message.trigger === 'turn_start') {
            return 4; // Helpful prompts
        }
        if (message.type === 'hint') {
            return 4; // Helpful hints
        }
        if (message.trigger) {
            return 2; // Other triggered messages
        }
        return 0; // Random/general messages
    }

    private getAvailableRandomMessages(stats: IUser): Message[] {
        return this.messages.filter(m => {
            // Skip triggered messages for random selection
            if (m.trigger) return false;

            // Apply progression filters
            if (m.progression) {
                if (m.progression.minGames && ((stats.gamesPlayed ?? 0) ?? 0) < m.progression.minGames) return false;
                if (m.progression.maxGames && ((stats.gamesPlayed ?? 0) ?? 0) > m.progression.maxGames) return false;
                if (m.progression.minRolls && stats.rollsTotal < m.progression.minRolls) return false;
                if (m.progression.maxRolls && stats.rollsTotal > m.progression.maxRolls) return false;
                if (m.progression.minSkeeches && stats.numOfSkeeches < m.progression.minSkeeches) return false;
                if (m.progression.maxSkeeches && stats.numOfSkeeches > m.progression.maxSkeeches) return false;
            }

            return true;
        });
    }

    /**
     * 🧹 CLEAR CURRENT MESSAGE
     * Force clear the current message (for game state changes, etc.)
     */
    public clearCurrentMessage() {
        this.currentMessagePriority = -1;
        this.currentMessageEndTime = 0;
        this.multipartMessageState = null;
        this.reservedMessageSlot = null; // Clear reservations too
    }

    /**
     * 🚫 RESERVE MESSAGE SLOT - Prevent conflicts by reserving space for expected messages
     * Call this BEFORE throwing the ball if you know a message is likely coming
     */
    public reserveMessageSlot(type: 'velocity' | 'score', velocityY?: number) {
        const now = performance.now();

        if (type === 'velocity' && velocityY !== undefined) {
            // Predict velocity message priority and duration
            let priority = 8;  // Default velocity message priority
            let duration = 2000;

            // Find the most specific velocity message that would trigger
            const velocityMessages = this.messages.filter(m =>
                m.trigger === 'high_velocity' || m.trigger === 'medium_velocity'
            ).sort((a, b) => (b.triggerValue || 0) - (a.triggerValue || 0)); // Sort by trigger value (highest first)

            for (const msg of velocityMessages) {
                if (msg.trigger === 'high_velocity' && velocityY < msg.triggerValue ||
                    msg.trigger === 'medium_velocity' && velocityY < msg.triggerValue && velocityY >= -425) {
                    priority = this.getMessagePriority(msg);
                    duration = msg.positioning?.duration || 2000;
                    break;
                }
            }

            this.reservedMessageSlot = {
                type: 'velocity',
                priority,
                reservedUntil: now + duration + 500 // Add buffer
            };
        } else if (type === 'score') {
            // Reserve for upcoming score message
            this.reservedMessageSlot = {
                type: 'score',
                priority: 10, // Score messages are always priority 10
                reservedUntil: now + 3000 // Reserve for 3 seconds
            };
        }
    }

    /**
     * 🚫 CHECK IF MESSAGE SLOT IS AVAILABLE
     * Returns false if a higher priority message is reserved or active
     */
    private isMessageSlotAvailable(priority: number): boolean {
        const now = performance.now();

        // Clear expired reservations
        if (this.reservedMessageSlot && now > this.reservedMessageSlot.reservedUntil) {
            this.reservedMessageSlot = null;
        }

        // Check if current message blocks this priority
        if (now < this.currentMessageEndTime && this.currentMessagePriority >= priority) {
            return false;
        }

        // Check if reserved slot blocks this priority
        if (this.reservedMessageSlot && this.reservedMessageSlot.priority >= priority) {
            return false;
        }

        return true;
    }

    /**
     * 📖 GET NEXT STORY MESSAGE
     * Returns the next part of an ongoing story
     */
    private getNextStoryMessage(stats: IUser): Message | null {
        const storyMessages = this.messages.filter(m => m.type === 'story');

        for (const message of storyMessages) {
            if (!message.story) continue;

            const storyId = message.story.storyId;
            const state = this.storyStates.get(storyId);

            // Check progression requirements
            if (!this.meetsProgressionRequirements(message, stats)) continue;

            // If no state, this is the first story message
            if (!state && message.story.sequence === 1) {
                return message;
            }

            // If state exists, check if this is the next expected message
            if (state && message.story.sequence === state.currentSequence + 1) {
                // Check if too many rolls have passed (story reset)
                const maxGap = message.story.maxGapRolls || 10;
                if (stats.rollsTotal - state.lastRollShown <= maxGap) {
                    return message;
                } else {
                    // Story timed out, reset
                    this.storyStates.delete(storyId);
                }
            }
        }

        return null;
    }

    /**
     * 📖 TRACK STORY PROGRESS
     * Updates the story state when a story message is shown
     */
    private trackStoryProgress(message: Message) {
        if (!message.story) return;

        const storyId = message.story.storyId;
        this.storyStates.set(storyId, {
            currentSequence: message.story.sequence,
            lastRollShown: Date.now(), // You'd want to track actual roll count
            totalRolls: 0 // This should be passed from game state
        });
    }

    /**
     * 🎛️ SET MESSAGE FREQUENCY
     * Adjust how often random messages appear (0.0 - 1.0)
     */
    public setMessageFrequency(frequency: number) {
        this.messageFrequency = Math.max(0, Math.min(1, frequency));
    }

    /**
     * 📖 RESET STORY
     * Reset a specific story or all stories
     */
    public resetStory(storyId?: string) {
        if (storyId) {
            this.storyStates.delete(storyId);
        } else {
            this.storyStates.clear();
        }
    }

    /**
     * 📊 GET STORY STATUS
     * Get current progress of all stories
     */
    public getStoryStatus(): Array<{storyId: string, currentSequence: number}> {
        return Array.from(this.storyStates.entries()).map(([storyId, state]) => ({
            storyId,
            currentSequence: state.currentSequence
        }));
    }

    private meetsProgressionRequirements(message: Message, stats: IUser): boolean {
        if (!message.progression) return true;

        if (message.progression.minGames && (stats.gamesPlayed ?? 0) < message.progression.minGames) return false;
        if (message.progression.maxGames && (stats.gamesPlayed ?? 0) > message.progression.maxGames) return false;
        if (message.progression.minRolls && stats.rollsTotal < message.progression.minRolls) return false;
        if (message.progression.maxRolls && stats.rollsTotal > message.progression.maxRolls) return false;
        if (message.progression.minSkeeches && stats.numOfSkeeches < message.progression.minSkeeches) return false;
        if (message.progression.maxSkeeches && stats.numOfSkeeches > message.progression.maxSkeeches) return false;

        return true;
    }
}

export const messageManager = new MessageManager();